import {Chess} from 'chess.js';
import {sanitizeFen} from './studyHelpers';
import type {PracticeGoal} from '../types';

/**
 * Pure outcome logic for "Practice with Computer" chapters.
 *
 * Lifted out of the Study screen (where it used to be an inline useMemo over
 * `moveSelected`) for three reasons:
 *  - it's the only part of practice mode worth unit-testing on its own;
 *  - the old version built `new Chess(fen)` from a single position, so it
 *    could never see threefold repetition or the 50-move rule (no history);
 *  - the old version's `if (!goalType) return ongoing` early-return sat ABOVE
 *    the stalemate check, so a no-goal chapter never reported a draw at all.
 *
 * Mirrors lila's studyPracticeCtrl ("null = ongoing, true = win, false =
 * fail"), widened to 'ongoing' | 'won' | 'lost' | 'drawn'.
 */

export type PracticeOutcomeStatus = 'ongoing' | 'won' | 'lost' | 'drawn';

export type PracticeOutcome = {
  status: PracticeOutcomeStatus;
  reason: string;
};

/**
 * Engine eval for the position produced by the student's LATEST move, already
 * converted to student POV.
 *
 * Deliberately not "the eval for the current position": the eval-driven goals
 * (mate / equalize / advantage) judge the position the student's move created,
 * and the opponent's reply lands long before the analysis engine has finished
 * searching it (the opponent budgets a few hundred ms, the analysis engine runs
 * to depth 24). Keying the goal check on the current position meant the reply
 * moved the goalposts and the win was missed outright - a student could sail
 * straight through the threshold and never be credited. The caller is
 * responsible for only passing an eval it has matched to the post-student-move
 * FEN; see usePracticeGame's `studentEval`.
 */
export type StudentEval = {
  /** Centipawns from the student's point of view (+ = student better). */
  cp: number;
  isMate: boolean;
};

export type ComputePracticeOutcomeArgs = {
  /** The chapter's starting position. */
  startFen: string;
  /** SANs the attempt has played, in order, from `startFen`. */
  sans: string[];
  /**
   * Just the STUDENT's own SANs, in order. Kept separate from `sans` because
   * both the promotion goal and the authored-line fallback have to ignore the
   * engine's replies entirely.
   */
  studentSans: string[];
  studentColor: 'w' | 'b';
  goal: PracticeGoal | null | undefined;
  /**
   * Latest engine eval, student POV — pass null unless it was computed for
   * the position the student's most recent move produced. Goal checks that
   * read the eval are skipped when it's null, rather than firing off a stale
   * number from a different position.
   */
  studentEval: StudentEval | null;
  /** How many moves the STUDENT has played (not counting engine replies). */
  studentMoveCount: number;
  /**
   * True when the attempt has followed the authored mainline to its end -
   * the fallback "win" for chapters with no explicit goal. See
   * `authoredLineExhausted`.
   */
  reachedEndOfAuthoredLine: boolean;
};

const ONGOING: PracticeOutcome = {status: 'ongoing', reason: ''};

// Thresholds match the goal labels shown in the chapter settings panel.
// Exported so the authoring copy and the check can never drift apart.
export const EQUALIZE_CP_THRESHOLD = -50;
export const ADVANTAGE_CP_THRESHOLD = 150;

/**
 * Replays `sans` from `startFen` so the resulting Chess carries real history -
 * without it, chess.js cannot detect threefold repetition, and a shuffling
 * engine produces a game that never ends. Returns null if the position or any
 * move is unplayable (a desynced attempt line), which callers treat as
 * "ongoing" rather than guessing.
 */
export function replayAttempt(startFen: string, sans: string[]): Chess | null {
  let game: Chess;
  try {
    game = new Chess(sanitizeFen(startFen));
  } catch (e) {
    return null;
  }
  for (const san of sans) {
    try {
      if (!game.move(san)) {
        return null;
      }
    } catch (e) {
      return null;
    }
  }
  return game;
}

/**
 * True when the student has played every authored move for THEIR OWN side, in
 * order. `tree` is the chapter's flat top-level array (which IS the mainline -
 * see moveTreeOps.ts).
 *
 * This used to compare the attempt ply-for-ply against the whole authored
 * line, engine replies included — which made it unreachable in practice and
 * left every "No explicit goal" chapter with no win condition at all. The
 * opponent here is a real engine searching to depth 5-18, not a scripted
 * partner: it deviates from the coach's line essentially immediately, and the
 * old check treated that as the STUDENT having left the line.
 *
 * Matching only the student's own moves is what the setting actually means -
 * "play this plan against real resistance" - and it is reachable however the
 * engine answers. When the engine does happen to follow the authored line, the
 * result is identical to the old behaviour.
 *
 * `studentPlyOffset` is which plies of the authored line belong to the
 * student: 0 when the student moves first from the chapter's start position,
 * 1 when the engine does.
 */
export function authoredLineExhausted(
  tree: {san?: string}[] | undefined,
  studentSans: string[],
  plyOffset: 0 | 1 = 0,
): boolean {
  if (!tree?.length || !studentSans.length) {
    return false;
  }
  const authoredStudentSans: string[] = [];
  for (let i = plyOffset; i < tree.length; i += 2) {
    const san = tree[i]?.san;
    if (san) {
      authoredStudentSans.push(san);
    }
  }
  if (!authoredStudentSans.length) {
    return false;
  }
  if (studentSans.length < authoredStudentSans.length) {
    return false;
  }
  for (let i = 0; i < authoredStudentSans.length; i++) {
    if (
      authoredStudentSans[i].toLowerCase() !== studentSans[i]?.toLowerCase()
    ) {
      return false;
    }
  }
  return true;
}

/** Which plies of the authored mainline the student is responsible for. */
export function studentPlyOffset(
  startFen: string,
  studentColor: 'w' | 'b',
): 0 | 1 {
  const turn = sanitizeFen(startFen).split(' ')[1] === 'b' ? 'b' : 'w';
  return turn === studentColor ? 0 : 1;
}

/**
 * A `mateIn` budget is only meaningful as a positive whole number of moves.
 * `goal.value` arrives from the chapter record, so 0/null/NaN all have to read
 * as "no budget set" rather than silently losing the attempt on move one.
 */
function mateInBudget(goal: PracticeGoal | null | undefined): number | null {
  const value = goal?.value;
  return typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : null;
}

function isDrawnPosition(game: Chess): boolean {
  // isDraw() covers stalemate, insufficient material, the 50-move rule and
  // threefold repetition - the last two only because `game` was replayed with
  // its full move history (see replayAttempt).
  try {
    return game.isDraw() || game.isStalemate();
  } catch (e) {
    return false;
  }
}

export function computePracticeOutcome({
  startFen,
  sans,
  studentSans,
  studentColor,
  goal,
  studentEval,
  studentMoveCount,
  reachedEndOfAuthoredLine,
}: ComputePracticeOutcomeArgs): PracticeOutcome {
  if (!sans.length) {
    return ONGOING;
  }

  const game = replayAttempt(startFen, sans);
  if (!game) {
    return ONGOING;
  }

  const turnNow = game.turn();
  const goalType = goal?.type;
  const mateIn = goalType === 'mateIn' ? mateInBudget(goal) : null;

  // --- Terminal positions, checked regardless of whether a goal is set ---
  if (game.isCheckmate()) {
    if (turnNow === studentColor) {
      return {status: 'lost', reason: 'You were checkmated.'};
    }
    // A mateIn goal is only met if the mate arrived within its budget.
    if (mateIn !== null && studentMoveCount > mateIn) {
      return {
        status: 'lost',
        reason: `Checkmate — but it took ${studentMoveCount} moves, not ${mateIn}.`,
      };
    }
    return {status: 'won', reason: 'Checkmate!'};
  }

  if (isDrawnPosition(game)) {
    return goalType === 'equalize'
      ? {status: 'won', reason: 'You held the draw.'}
      : {status: 'drawn', reason: 'The game was drawn.'};
  }

  // Ran out of the budget for "checkmate in N" without delivering mate.
  if (mateIn !== null && studentMoveCount >= mateIn) {
    return {
      status: 'lost',
      reason: `No checkmate within ${mateIn} move${mateIn > 1 ? 's' : ''}.`,
    };
  }

  // --- Goal-specific wins ---
  //
  // Deliberately NOT gated on "the student just moved". The eval-driven goals
  // judge the position the student's last move produced, and `studentEval` is
  // already pinned to exactly that position by the caller - so the check stays
  // valid after the opponent has replied. Requiring the student to still be
  // the last mover meant the reply (which lands in a few hundred ms, long
  // before a depth-24 search finishes) closed the window before the eval ever
  // arrived, and the win was silently lost.
  if (goalType) {
    if (goalType === 'promotion') {
      // The student's own last move, not the attempt's - otherwise the ENGINE
      // promoting a pawn would win the exercise for the student.
      const lastStudentSan = studentSans[studentSans.length - 1] || '';
      if (/[=][QRBN]/.test(lastStudentSan)) {
        return {status: 'won', reason: 'You promoted a pawn!'};
      }
    }
    if (studentEval) {
      if (goalType === 'mate' && studentEval.isMate && studentEval.cp > 0) {
        return {status: 'won', reason: 'A forced mate is on the board!'};
      }
      if (goalType === 'equalize' && studentEval.cp >= EQUALIZE_CP_THRESHOLD) {
        return {status: 'won', reason: 'You equalized the position!'};
      }
      if (
        goalType === 'advantage' &&
        studentEval.cp >= ADVANTAGE_CP_THRESHOLD
      ) {
        return {status: 'won', reason: 'You reached a winning advantage!'};
      }
    }
  }

  // --- No explicit goal: the fallback is the authored line running out ---
  if (!goalType && reachedEndOfAuthoredLine) {
    return {
      status: 'won',
      reason: "You've reached the end of the line. Well played!",
    };
  }

  return ONGOING;
}
